import { LightButton } from "@/ui/Button"
import { Image } from "@heroui/react"
import { useCallback, useEffect, useLayoutEffect, useState, useTransition } from "react"
import { AuthorizationError } from "@/errors/AuthorizationError"
import { useLocale, useTranslations } from "next-intl"
import { useUserStore } from "@/store/useUserStore"
import { useUserDetailStore } from "@/store/useUserDetailStore"
import { useRouter } from "next/navigation"
import { reportError } from "@/vendor/arms"
import { useCopy } from "@/hooks/useCopy"
import { redirectToLogin } from "@/util/loginRedirect"

// 发布新版本后旧 Pod 销毁，正开着旧页面的用户导航时会请求已不存在的旧 chunk，
// 触发 ChunkLoadError。整页刷新拿到新版本 HTML 即可恢复，无需用户手动操作。
const CHUNK_RELOAD_KEY = '__chunk_err_reload_at__'

function isChunkLoadError(error: Error) {
  return error.name === 'ChunkLoadError'
    || /Loading chunk .+ failed|Loading CSS chunk .+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i
      .test(error.message ?? '')
}

// 返回 true 表示即将整页刷新，调用方应渲染空白避免闪现错误画面。
// 60 秒内只自动刷新一次：若刷新后仍报错（如网络本身有问题），退回展示错误页，避免刷新死循环
export function useChunkErrorAutoReload(error: Error) {
  const [autoReloading] = useState(() => {
    if (typeof window === 'undefined' || !isChunkLoadError(error)) return false
    return Date.now() - Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0) > 60_000
  })

  useEffect(() => {
    if (!autoReloading) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    window.location.reload()
  }, [autoReloading])

  return autoReloading
}

export default function ErrorHandler({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const t = useTranslations()
  const locale = useLocale()
  let btnTitle = t('common.retry')
  let message = error.message

  const autoReloading = useChunkErrorAutoReload(error)

  // 业务可预期的错误（鉴权/网络/参数等）无需上报，仅上报真正的异常
  useEffect(() => {
    const expected = ["AuthorizationError", "NetworkError", "SearchParamsError", "ServerError"]
    if (!expected.includes(error.name)) {
      reportError(error)
    }
  }, [error])

  const [isPending, startTransition] = useTransition()
  const router = useRouter()

  // 真实错误原因文本（含 digest），供展示与一键复制反馈
  const detail = [
    [error?.name, error?.message].filter(Boolean).join(': '),
    error?.digest ? `#${error.digest}` : '',
  ].filter(Boolean).join('  ·  ')

  const { copy } = useCopy()
  const [copied, setCopied] = useState(false)
  const onCopyDetail = useCallback(() => {
    if (!detail) return
    copy(detail, {
      onSucess() {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
    })
  }, [detail])
  const unLogin = useUserStore(state => state.unLogin)
  const clear = useUserDetailStore(state => state.clearDetail)
  const isAuthError = error instanceof AuthorizationError

  // 渲染期抛出的鉴权错误走错误边界，这里直接去带 redirect 的登录页，不再停在错误页
  useLayoutEffect(() => {
    if (!isAuthError) return
    unLogin()
    clear()
    redirectToLogin(locale)
  }, [isAuthError, locale, unLogin, clear])

  const reload = useCallback(() => {
    startTransition(() => {
      router.refresh()
      reset()
    })
  }, [])

  const catchError = ["AuthorizationError",
    "NetworkError",
    "SearchParamsError",
    "ServerError"]

  if (catchError.includes(error.name)) message = t(`error.${error.name}`)
  else message = t(`error.NetworkError`)

  // 即将整页刷新或跳登录，不闪现错误画面
  if (autoReloading || isAuthError) return null


  return (
    <div className='flex flex-col items-center '>
      <Image
        src="/imgs/ServerError.svg"
        width={240}
        height={240}
        alt="ServerError Img" />
      <h2 className='max-w-[600px] px-4 text-center'>{message}</h2>
      {/* 真实错误原因（小字）：便于用户截图反馈时直接定位是网络/鉴权/代码异常，
          digest 可与 ARMS / 服务端日志按编号对应 */}
      {detail && (
        <div className='mt-2 flex max-w-[600px] flex-col items-center gap-1 px-4'>
          <p className='text-center text-xs leading-relaxed text-theme-primary/40 break-words'>
            {detail}
          </p>
          <button
            type='button'
            onClick={onCopyDetail}
            className='text-xs text-theme-primary/40 underline-offset-2 transition-colors hover:text-theme-primary/70 hover:underline'
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
      <LightButton
        isLoading={isPending}
        className='mt-[20px] min-w-[200px]'
        onPress={reload}>{btnTitle}</LightButton>
    </div>
  )
}